import { Injectable } from '@angular/core';
import { Observable, EMPTY } from 'rxjs';
import { PortalApiRepository } from '../../../../core/repositories/portal-api-repository';
import { CurrentUserState } from '../../../../core/store/current-user/current-user.state';
import {
  LearningPlanInstancesResponseDTO,
  LearningPlanInstanceEnrollmentsResponseDTO,
  SendReminderRequestDTO,
  LearningPlanEnrollmentRequestDTO,
  LearningPlanUnenrollmentRequestDTO,
  LearningPlanInstanceDetailResponseDTO,
  TypeTopicAreasResponse,
  TypeLearningPlanInstancePOST,
  CreateLearningPlanInstanceResponseDTO,
  LearningPlanInstanceCourseUserActivitiesResponseDTO,
} from '../models/LearningPlanDTO';
import {
  toLearningPlanEnrollmentRequestDTO,
  toLearningPlanUnenrollmentRequestDTO,
  toSendReminderRequestDTO,
} from '../mappers/dto-mappers';
import { LearningPlanEnrolledStudentUIModel } from '../models/LearningPlanEnrolledStudentUIModel';
import { HttpParams } from '@angular/common/http';
import { PrecuratedLearningPlanDetailsDto } from '../models/PrecuratedLearningPlanDTO';
import { UpdateRepetitionRequest } from '../models/UpdateRepetitionRequest';

@Injectable({
  providedIn: 'root',
})
export class LearningPlanService extends PortalApiRepository {
  constructor() {
    super();
  }

  getLearningPlanInstances(): Observable<LearningPlanInstancesResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances`;

    return this.get<LearningPlanInstancesResponseDTO>(path);
  }

  getLearningPlanInstanceDetails(
    id: string,
  ): Observable<LearningPlanInstanceDetailResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${id}`;

    return this.get<LearningPlanInstanceDetailResponseDTO>(path);
  }

  getLearningPlanEnrollments(
    id: string,
  ): Observable<LearningPlanInstanceEnrollmentsResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${id}/enrollments`;

    return this.get<LearningPlanInstanceEnrollmentsResponseDTO>(path);
  }

  getLearningPlanCourseUserActivities(
    id: string,
    startDate: string,
    endDate: string,
  ): Observable<LearningPlanInstanceCourseUserActivitiesResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${id}/courseUserActivities/lastInPeriod`;
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.get<LearningPlanInstanceCourseUserActivitiesResponseDTO>(
      path,
      params,
    );
  }

  enrollStudentsToLearningPlan(
    students: Array<{ id: string }>,
    learningPlanInstanceId: string,
  ): Observable<void> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${learningPlanInstanceId}/enroll`;
    const payload = toLearningPlanEnrollmentRequestDTO(students);

    return this.post<LearningPlanEnrollmentRequestDTO, void>(path, payload);
  }

  unenrollStudentsFromLearningPlan(
    students: LearningPlanEnrolledStudentUIModel[],
    learningPlanInstanceId: string,
  ): Observable<void> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${learningPlanInstanceId}/unenroll`;
    const payload = toLearningPlanUnenrollmentRequestDTO(students);

    return this.post<LearningPlanUnenrollmentRequestDTO, void>(path, payload);
  }

  sendReminder(
    students: LearningPlanEnrolledStudentUIModel[],
  ): Observable<void> {
    const path = `/send-reminder/manual`;
    const payload = toSendReminderRequestDTO(students);

    return this.post<SendReminderRequestDTO, void>(path, payload);
  }

  createLearningPlanInstance(
    learningPlanInstance: TypeLearningPlanInstancePOST,
  ): Observable<CreateLearningPlanInstanceResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances`;
    return this.post<
      TypeLearningPlanInstancePOST,
      CreateLearningPlanInstanceResponseDTO
    >(path, learningPlanInstance);
  }

  createLearningPlanInstanceFromSetup(
    learningPlanInstance: TypeLearningPlanInstancePOST,
  ): Observable<CreateLearningPlanInstanceResponseDTO> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/setup`;
    return this.post<
      TypeLearningPlanInstancePOST,
      CreateLearningPlanInstanceResponseDTO
    >(path, learningPlanInstance);
  }

  editLearningPlanInstance(
    learningPlanInstanceId: string,
    learningPlanInstance: TypeLearningPlanInstancePOST,
  ): Observable<void> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${learningPlanInstanceId}/`;
    return this.put<TypeLearningPlanInstancePOST, void>(
      path,
      learningPlanInstance,
    );
  }

  updateLearningPlanInstanceRepetition(
    learningPlanInstanceId: string,
    request: UpdateRepetitionRequest,
  ): Observable<void> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${learningPlanInstanceId}/repetition`;
    return this.put<UpdateRepetitionRequest, void>(path, request);
  }

  deleteLearningPlanInstance(learningPlanInstanceId: string): Observable<void> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/LearningPlanInstances/${learningPlanInstanceId}/`;
    return this.delete(path);
  }

  getTopicAreas(): Observable<TypeTopicAreasResponse> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    if (!subscriptionId) {
      return EMPTY;
    }

    const path = `/subscriptions/${subscriptionId}/TopicAreas`;
    return this.get<TypeTopicAreasResponse>(path);
  }

  exportStudentInfoToCsv(
    learningPlanInstanceId: string,
    startDate: string,
    endDate: string,
  ): Observable<Blob> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/learningplaninstances/${learningPlanInstanceId}/report`;
    const params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    return this.download(path, params);
  }

  getPrecuratedLearningPlans(
    topicAreaId: string,
  ): Observable<PrecuratedLearningPlanDetailsDto[]> {
    const subscriptionId = this.store.selectSnapshot(
      CurrentUserState.subscriptionId,
    );
    const path = `/subscriptions/${subscriptionId}/PrecuratedLearningPlan/${topicAreaId}`;

    return this.get<PrecuratedLearningPlanDetailsDto[]>(path);
  }
}
